import type { EraDef } from '../types';

/**
 * ERA 1 — PRIMORDIAL SOUP
 * Microbes, viruses, amoebas. Boss: the Colony (super-organism).
 * Palette: murky teal water, bioluminescent green/yellow accents. Enemy bullets: magenta.
 *
 * Movement identity: "things come from underground / the ooze" — signature
 * behaviors are `burrow` (amoebas sink into the soup and re-emerge beside
 * you) and `zigzag` (flagellates weaving). Pattern identity: slow fat orbs
 * with `wave`, and spore clouds that `linger`. Era hazard: lingering spore
 * pools, on the ground and thrown by the boss — plus exactly one elite
 * enemy (the mother-cell phage) leaves a thin, low-dps acid `trail`.
 * Boss (The Colony, `p1_colony`) mechanic: phase 2 `shield` (kill 4
 * `p1_polyp` adds to break its invulnerability), phase 3 `burrow` (the
 * boss itself dives into the ooze) + an `onEnter` radial spore-ring burst.
 */
const ENEMY_BULLET = 0xff3fb0;
const HAZARD = 0x9dff3a;

export const ERA_PRIMORDIAL: EraDef = {
  index: 0,
  id: 'primordial',
  name: 'Primordial Soup',
  subtitle: '3.8 billion years ago',
  implemented: true,
  palette: {
    sky: 0x06201f,
    fog: 0x0a2c2a,
    fogDensity: 0.018,
    ground: 0x0f3d3a,
    groundAlt: 0x155a52,
    ambient: 0x1f6b66,
    sun: 0x9be8c8,
    sunIntensity: 1.4,
    accent: 0x5cf0ff,
    enemyBullet: ENEMY_BULLET,
    hazard: HAZARD,
  },
  environment: {
    kind: 'soup',
    arenaRadius: 60,
    groundStyle: 'cells',
    props: [
      { visual: { geo: 'blob', color: 0x1c7a6a, emissive: 0x0d3d34, emissiveIntensity: 0.4, anim: 'pulse' }, count: 40, scale: [0.6, 1.8], y: 0 },
      { visual: { geo: 'capsule', color: 0x2aa58a, scaleY: 3, anim: 'wobble' }, count: 25, scale: [0.3, 0.7], y: 0.8 },
      { visual: { geo: 'sphere', color: 0x7dffd8, emissive: 0x7dffd8, emissiveIntensity: 1.2, anim: 'bob' }, count: 60, scale: [0.08, 0.2], y: 1.5 },
    ],
  },
  enemies: [
    // Swarmer filler: runs straight at you, weak alone, dangerous in numbers. Counterplay: kill on sight, don't let them stack.
    {
      id: 'p1_microbe',
      name: 'Microbe',
      archetype: 'swarmer',
      hp: 12,
      speed: 3.0,
      damage: 5,
      radius: 0.45,
      xp: 4,
      visual: { geo: 'sphere', color: 0x62d86b, emissive: 0x1d5a22, emissiveIntensity: 0.5, scale: 0.9, anim: 'wobble' },
      behavior: { type: 'chase' },
    },
    // Shooter: kites at range and lobs slow, fat, weaving orbs (pattern identity: negative-accel + wave). Counterplay: the wave telegraphs early, walk to either side of the arc or close the distance to cut off the shot.
    {
      id: 'p1_virus',
      name: 'Virus',
      archetype: 'shooter',
      hp: 22,
      speed: 2.2,
      damage: 6,
      radius: 0.5,
      xp: 6,
      visual: { geo: 'spike', color: 0xd94fd0, emissive: 0x6a1a66, emissiveIntensity: 0.6, scale: 1, anim: 'spin' },
      behavior: { type: 'keepDistance', distance: 9, strafe: 0.35 },
      patterns: ['p1_orb_wave'],
      patternCooldown: 2,
      fireRange: 16,
    },
    // Signature `zigzag`: weaves side to side while closing in, hard to lead with slow shots. Counterplay: use fast/homing hits or wait for the weave to swing it into your line of fire.
    {
      id: 'p1_flagellate',
      name: 'Flagellate',
      archetype: 'charger',
      hp: 34,
      speed: 3.2,
      damage: 10,
      radius: 0.55,
      xp: 8,
      visual: { geo: 'cone', color: 0xf0c84a, emissive: 0x6b5410, emissiveIntensity: 0.4, scale: 1.1, scaleY: 1.6 },
      behavior: { type: 'zigzag', period: 0.9, amp: 0.8 },
    },
    // Signature `burrow`: sinks into the ooze and re-emerges beside you (emerge flash + 0.4s contact grace telegraph the ambush). Counterplay: watch for the emerge flash, punish while it's surfaced, back off when its timer is about to run out.
    {
      id: 'p1_amoeba',
      name: 'Amoeba',
      archetype: 'tank',
      hp: 110,
      speed: 1.6,
      damage: 16,
      radius: 1.1,
      xp: 18,
      shardChance: 0.15,
      visual: { geo: 'blob', color: 0x3fb59a, emissive: 0x11493d, emissiveIntensity: 0.5, scale: 2, anim: 'pulse' },
      behavior: { type: 'burrow', surfaceTime: 3.5, burrowTime: 2, speedMult: 2.2, emergeRange: 4.5 },
      onDeath: { spawn: { enemy: 'p1_microbe', count: 4, spread: 1.4 } },
    },
    // Spore-cloud tank/splitter: slow bulky drifter that periodically vents a lingering spore cloud while alive and bursts into one on death (era hazard theme). Counterplay: don't stand in the green pools; it's harmless until it pops or vents.
    {
      id: 'p1_spore',
      name: 'Spore Cyst',
      archetype: 'splitter',
      hp: 55,
      speed: 1.0,
      damage: 8,
      radius: 0.75,
      xp: 10,
      visual: { geo: 'octa', color: 0xb8ff5a, emissive: 0x5d8f1b, emissiveIntensity: 0.9, scale: 1.3, anim: 'bob' },
      behavior: { type: 'drift', jitter: 0.6 },
      patterns: ['p1_spore_release'],
      patternCooldown: 4,
      fireRange: 30,
      onDeath: { pattern: 'p1_spore_burst' },
    },
    // Elite rarity "mother cell": orbits and fires, leaves a thin low-dps acid trail while it moves (this era's only trailing enemy), and splits into microbes on death. Counterplay: don't hug it, step wide around its trail, mop up the splits.
    {
      id: 'p1_phage',
      name: 'Bacteriophage',
      archetype: 'elite',
      hp: 60,
      speed: 2.8,
      damage: 12,
      radius: 0.7,
      xp: 20,
      shardChance: 0.4,
      elite: true,
      visual: { geo: 'spike', color: 0xff5cf5, emissive: 0xff5cf5, emissiveIntensity: 1, scale: 1.2, anim: 'spin' },
      behavior: { type: 'orbit', radius: 8, angular: 0.6 },
      patterns: ['p1_spiral', 'p1_wall'],
      patternCooldown: 1.2,
      fireRange: 18,
      trail: { radius: 0.5, duration: 2.5, dps: 3, interval: 0.35, color: HAZARD },
      onDeath: { spawn: { enemy: 'p1_microbe', count: 3, spread: 1.2 } },
    },
    // Boss shield add (The Colony, phase 2 "Division"): stationary polyp that pops out of the boss's mass and keeps it invulnerable until all 4 are dead. Counterplay: ignore the boss, kill the polyps; each one only lobs a small aimed spit.
    {
      id: 'p1_polyp',
      name: 'Polyp',
      archetype: 'shooter',
      hp: 60,
      speed: 0,
      damage: 6,
      radius: 0.5,
      xp: 8,
      visual: { geo: 'torus', color: 0x49d6a8, emissive: 0x1f7a5c, emissiveIntensity: 0.7, scale: 1, anim: 'wobble' },
      behavior: { type: 'stationary' },
      patterns: ['p1_aimed3'],
      patternCooldown: 1.5,
      fireRange: 20,
    },
  ],
  patterns: [
    {
      id: 'p1_aimed3',
      emitters: [{ shape: 'aimed', interval: 0.12, bursts: 3, count: 1, bullet: { speed: 7, lifetime: 3, radius: 0.22, damage: 8, visual: 'orb', color: ENEMY_BULLET } }],
    },
    {
      id: 'p1_radial8',
      emitters: [{ shape: 'radial', interval: 1, bursts: 1, count: 8, bullet: { speed: 4.5, lifetime: 4, radius: 0.25, damage: 9, visual: 'orb', color: ENEMY_BULLET } }],
    },
    {
      id: 'p1_spore_burst',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 6, angle: 0.3, bullet: { speed: 3, accel: -1.2, minSpeed: 0.4, lifetime: 2.2, radius: 0.28, damage: 7, visual: 'blob', color: HAZARD, linger: { radius: 0.9, duration: 3, dps: 6, color: HAZARD } } },
      ],
    },
    // Pattern identity: slow fat orb, decelerating, with a sideways wave — the virus's signature shot.
    {
      id: 'p1_orb_wave',
      emitters: [
        { shape: 'aimed', interval: 1.6, bursts: 2, count: 1, bullet: { speed: 3.2, accel: -0.4, minSpeed: 1.4, lifetime: 4.5, radius: 0.4, damage: 9, visual: 'orb', color: ENEMY_BULLET, wave: { amp: 1.3, freq: 0.55 } } },
      ],
    },
    // Periodic small spore-cloud vent (era hazard theme) — fired by the Spore Cyst while alive, not just on death.
    {
      id: 'p1_spore_release',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 4, bullet: { speed: 2, accel: -1.5, minSpeed: 0.3, lifetime: 2.2, radius: 0.3, damage: 5, visual: 'blob', color: HAZARD, linger: { radius: 0.8, duration: 2.5, dps: 4, color: HAZARD } } },
      ],
    },
    {
      id: 'p1_spiral',
      duration: 2.4,
      emitters: [{ shape: 'spiral', interval: 0.08, bursts: 0, count: 2, rotatePerBurst: 0.31, bullet: { speed: 5.5, lifetime: 3.5, radius: 0.22, damage: 8, visual: 'shard', color: ENEMY_BULLET } }],
    },
    {
      id: 'p1_wall',
      emitters: [{ shape: 'wall', interval: 0.5, bursts: 2, count: 11, spread: 9, gaps: 3, bullet: { speed: 4, lifetime: 4, radius: 0.28, damage: 10, visual: 'ring', color: ENEMY_BULLET } }],
    },
    // ---- boss patterns ----
    {
      id: 'p1_boss_ring',
      emitters: [
        { shape: 'radial', interval: 0.5, bursts: 4, count: 22, rotatePerBurst: 0.17, bullet: { speed: 4.8, lifetime: 5, radius: 0.28, damage: 10, visual: 'orb', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'p1_boss_double_spiral',
      duration: 3.6,
      emitters: [
        { shape: 'spiral', interval: 0.06, bursts: 0, count: 3, rotatePerBurst: 0.22, bullet: { speed: 5.6, lifetime: 4, radius: 0.24, damage: 9, visual: 'shard', color: ENEMY_BULLET } },
        { shape: 'spiral', interval: 0.06, bursts: 0, count: 3, rotatePerBurst: -0.22, angle: 0.5, bullet: { speed: 5.6, lifetime: 4, radius: 0.24, damage: 9, visual: 'shard', color: 0xff8ad0 } },
      ],
    },
    {
      id: 'p1_boss_homing',
      emitters: [
        { shape: 'radial', interval: 0.35, bursts: 5, count: 6, angle: 0.4, bullet: { speed: 4, lifetime: 5, radius: 0.32, damage: 13, homing: 1.8, homingDelay: 0.6, visual: 'blob', color: 0xff5cf5 } },
      ],
    },
    {
      id: 'p1_boss_walls',
      emitters: [
        { shape: 'wall', interval: 0.75, bursts: 5, count: 19, spread: 16, gaps: 3, bullet: { speed: 5.2, lifetime: 5, radius: 0.3, damage: 11, visual: 'ring', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'p1_boss_spore_rain',
      emitters: [
        { shape: 'random', interval: 0.2, bursts: 10, count: 4, spread: 6.283, bullet: { speed: 6, accel: -3, minSpeed: 0.5, lifetime: 2, radius: 0.3, damage: 8, visual: 'blob', color: HAZARD, linger: { radius: 1.2, duration: 4, dps: 8, color: HAZARD } } },
      ],
    },
    // Phase 3 onEnter burst: a big radial ring of spore blobs that carpet the arena in lingering pools the instant the boss surfaces from its dive.
    {
      id: 'p1_boss_spore_ring',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 20, bullet: { speed: 3.5, accel: -1, minSpeed: 0.6, lifetime: 3, radius: 0.35, damage: 10, visual: 'blob', color: HAZARD, linger: { radius: 1.1, duration: 3.5, dps: 7, color: HAZARD } } },
      ],
    },
  ],
  boss: {
    id: 'p1_colony',
    name: 'The Colony',
    title: 'Primordial Super-Organism',
    hp: 2600,
    radius: 2.1,
    speed: 1.7,
    damage: 26,
    xp: 150,
    visual: { geo: 'blob', color: 0x49d6a8, emissive: 0x1f7a5c, emissiveIntensity: 0.8, scale: 1.5, anim: 'pulse' },
    phases: [
      // Phase 1 "Bloom": teaching phase — the boss's base pattern kit at the tuned pace, no mechanic yet.
      { hpBelow: 1, name: 'Bloom', behavior: { type: 'keepDistance', distance: 10, strafe: 0.25 }, patterns: ['p1_boss_ring', 'p1_boss_walls', 'p1_boss_homing'], patternCooldown: 1.1, transition: 1.5 },
      // Phase 2 "Division": `shield` mechanic — 4 p1_polyp adds spawn on entry and the boss is invulnerable until they're dead.
      // The boss itself keeps its distance and fires only one, slower pattern so the player can safely leave it to go hunt the polyps.
      {
        hpBelow: 0.7,
        name: 'Division',
        behavior: { type: 'keepDistance', distance: 11, strafe: 0.15 },
        patterns: ['p1_boss_ring'],
        patternCooldown: 1.8,
        transition: 2,
        speedMult: 0.9,
        special: { type: 'shield', enemy: 'p1_polyp', count: 4 },
      },
      // Phase 3 "Fever": the boss adopts the era's signature `burrow` — dives into the ooze and surfaces beside the player —
      // and its surfacing is punctuated by a big onEnter spore-ring burst, combined with the densest pattern kit.
      {
        hpBelow: 0.35,
        name: 'Fever',
        behavior: { type: 'burrow', surfaceTime: 4, burrowTime: 2.5, speedMult: 1.6, emergeRange: 5 },
        patterns: ['p1_boss_spore_rain', 'p1_boss_double_spiral', 'p1_boss_walls'],
        patternCooldown: 0.5,
        summons: { enemy: 'p1_spore', count: 4, interval: 4 },
        transition: 2,
        onEnter: 'p1_boss_spore_ring',
      },
    ],
  },
  stages: [
    {
      name: 'Tide Pool',
      duration: 180,
      objective: { type: 'survive', seconds: 180 },
      density: { start: 0.6, end: 2.0 },
      pool: [
        { enemy: 'p1_microbe', weight: 10 },
        { enemy: 'p1_virus', weight: 4, minTime: 25 },
        { enemy: 'p1_spore', weight: 3, minTime: 45 },
        { enemy: 'p1_flagellate', weight: 2, minTime: 70 },
      ],
      waves: [
        { t: 4, enemy: 'p1_microbe', count: 5, formation: 'edge' },
        { t: 30, enemy: 'p1_virus', count: 4, formation: 'line' },
        { t: 60, enemy: 'p1_microbe', count: 16, formation: 'cluster' },
        { t: 90, enemy: 'p1_flagellate', count: 3, formation: 'edge' },
        { t: 120, enemy: 'p1_amoeba', count: 1, formation: 'edge' },
        { t: 150, enemy: 'p1_microbe', count: 24, formation: 'ring' },
        { t: 165, enemy: 'p1_phage', count: 1, formation: 'edge', elite: true },
      ],
    },
    {
      name: 'Thermal Vent',
      duration: 210,
      objective: { type: 'kill', count: 140 },
      density: { start: 1.1, end: 2.6 },
      pool: [
        { enemy: 'p1_microbe', weight: 8 },
        { enemy: 'p1_virus', weight: 5 },
        { enemy: 'p1_spore', weight: 4 },
        { enemy: 'p1_flagellate', weight: 4, minTime: 20 },
        { enemy: 'p1_amoeba', weight: 1, minTime: 60 },
      ],
      waves: [
        { t: 5, enemy: 'p1_flagellate', count: 5, formation: 'ring' },
        { t: 40, enemy: 'p1_amoeba', count: 2, formation: 'line' },
        { t: 75, enemy: 'p1_virus', count: 8, formation: 'ring' },
        { t: 110, enemy: 'p1_phage', count: 1, formation: 'edge', elite: true },
        { t: 140, enemy: 'p1_microbe', count: 30, formation: 'ring' },
        { t: 170, enemy: 'p1_amoeba', count: 3, formation: 'cluster' },
        { t: 195, enemy: 'p1_phage', count: 2, formation: 'line', elite: true },
      ],
    },
    {
      name: 'The Colony',
      duration: 600,
      density: { start: 1.2, end: 1.2 },
      pool: [
        { enemy: 'p1_microbe', weight: 6 },
        { enemy: 'p1_virus', weight: 3 },
      ],
      waves: [{ t: 2, enemy: 'p1_microbe', count: 10, formation: 'ring' }],
      boss: { id: 'p1_colony', at: 18 },
    },
  ],
  loot: [
    { itemId: 'w_spore_launcher', weight: 1 },
    { itemId: 'a_membrane', weight: 1 },
    { itemId: 'r_flagellum', weight: 1 },
  ],
  audio: { music: 'music/era1_soup', ambient: 'amb/bubbles', sfxSet: 'organic' },
};
